const SECTIONS = {
  ANAMNESE_GERAL: "ANAMNESE GERAL",
  ANAMNESE_NEUROLOGICA: "ANAMNESE NEUROLOGICA",
  CLASSIFICACAO_ELA_SIM: "CLASSIFICAÇÃO DA ELA",
  CLASSIFICACAO_ELA_NAO: "CLASSIFICAÇÃO DA ELA",
  CONDUTAS: "CONDUTAS",
};

const FIELDS = {
  ANAMNESE_NEUROLOGICA: [
    { label: "Anamnese:", key: "anamnese" },
    { label: "Sinais e Sintomas:", key: "sinaisSintomas" },
  ],
  ANAMNESE_GERAL: [
    { label: "Frequência cardíaca:", key: "frequenciaCardiaca", und: "bpm" },
    { label: "Pressão arterial:", key: "pressaoArterial", und: "" },
    { label: "Peso atual:", key: "pesoAtual", und: "Kg" },
    { label: "Perda de peso:", key: "perdaDePeso", und: "%" },
    { label: "Fluência verbal:", key: "fluenciaVerbal", und: "" },
    {
      label: "Rastreio comportamental (ECAS):",
      key: "rastreioComportamental",
      und: "",
    },
    { label: "Apatia:", key: "apatia", und: "" },
    { label: "Status cognitivo:", key: "statusCognitivo", und: "" },
    {
      label: `\n\nANAMNESE GERAL - Função Pulmonar`,
      key: "",
    },
    {
      label: "Capacidade Vital Forçada (CVF):",
      key: "capacidadeVitalForcada",
      und: "%",
    },
    {
      label: "Pressão Inspiratória (PI):",
      key: "pressaoInspiratoria",
      und: "cmH2O",
    },
    {
      label: "Pressão Expiratória (PE):",
      key: "pressaoExpiratoria",
      und: "cmH2O",
    },
    {
      label: "Pressão Nasal Inspiratória (SNIP):",
      key: "pressaoNasalInspiratoria",
      und: "cmH2O",
    },
  ],
  CLASSIFICACAO_ELA_SIM: [
    { label: "O diagnóstico é ELA?", key: "diagnosticoEhELA" },
    { label: "Data do diagnóstico:", key: "dataDiagnosticoELA" },
    {
      label: "Classificação King's College:",
      key: "classificacaoKingsCollege",
    },
    { label: "Escala funcional ALSFRS-R:", key: "escalaFuncional" },
    { label: "Velocidade de progressão:", key: "velocidadeDeProgressao" },
    { label: "Causa:", key: "causaELA" },
    { label: "Local de início:", key: "localDeInicio" },
    { label: "Fenótipo de início:", key: "fenotipoDeInicio" },
    { label: "Observações:", key: "observacaoELA" },
  ],
  CLASSIFICACAO_ELA_NAO: [
    { label: "O diagnóstico é ELA?", key: "diagnosticoEhELA" },
    { label: "Sugestão de diagnóstico:", key: "sugestaoDiagnostico" },
    { label: "Observações:", key: "observacaoELA" },
  ],
  CONDUTAS: [{ label: "Condutas:", key: "condutaConclusaoEncaminhamentos" }],
};

/**
 * Gera uma seção para o relatório de consulta com base na seção informada e nos dados.
 *
 * @param {string} section - A seção a ser gerada.
 * @param {Object} data - Os dados a serem utilizados para gerar a seção.
 * @returns {string} A seção gerada.
 */
const generateSection = (section, data) => {
  const fields = FIELDS[section];
  const content = fields
    .map((field) => {
      let value = data[field.key] ?? "";
      if (Array.isArray(value)) {
        value = value.join("\n");
      }
      if (field.und) {
        value = `${value} ${field.und}`;
      }

      return `${field.label} \n${value}\n\n`;
    })
    .join("");
  return `${SECTIONS[section]}\n\n${content}`;
};

/**
 * Gera o conteúdo do arquivo de relatório, com base nos campos
 * selecionados pelo usuário.
 *
 * @param {Object} data - Dados da consulta.
 * @param {Object} exportSelected - Campos selecionados para exportação.
 * @return {String} Conteúdo do arquivo de relatório.
 */
const generateFileContent = (data, exportSelected) => {
  const sections = [];
  if (exportSelected.anamneseGeral) {
    sections.push(
      "---------------------------------------------------------\n"
    );
    sections.push(generateSection("ANAMNESE_GERAL", data));
  }
  if (exportSelected.anamneseNeurologica) {
    sections.push(
      "---------------------------------------------------------\n"
    );
    sections.push(generateSection("ANAMNESE_NEUROLOGICA", data));
  }

  if (exportSelected.classificacaoELA) {
    sections.push(
      "---------------------------------------------------------\n"
    );
    if (
      data.diagnosticoEhELA === "Sim" ||
      data.diagnosticoEhELA === "Em investigação"
    ) {
      sections.push(generateSection("CLASSIFICACAO_ELA_SIM", data));
    } else {
      sections.push(generateSection("CLASSIFICACAO_ELA_NAO", data));
    }
  }
  if (exportSelected.condutas) {
    sections.push(
      "---------------------------------------------------------\n"
    );
    sections.push(generateSection("CONDUTAS", data));
  }
  return sections.join("\n\n");
};

/**
 * Cria um arquivo de texto com o resumo da consulta
 * @param {number} id - ID da consulta
 * @param {object} headerData - Dados do cabeçalho da consulta
 * @param {object} dadosIniciais - Dados iniciais da consulta
 * @param {object} exportSelected - Seções a serem exportadas
 * @returns {void}
 */
export const createFileTxt = (
  id,
  headerData,
  dadosIniciais,
  exportSelected
) => {
  // Conteúdo do arquivo
  const title = `Detalhes-da-consulta-neurológica-ID-${id}`;
  // Cabeçalho
  let body = "";
  body += `Detalhes da consulta neurológica ID ${id} \n`;
  body += `Paciente: ${headerData.paciente.nome} \n`;
  body += `Profissional responsável: ${headerData.responsavel.nome} \n`;
  body += `CRM/UF: ${headerData.responsavel?.crm ?? "NULL"}/${
    headerData.responsavel?.crmUF ?? "NULL"
  } \n`;
  body += `Realizado em formato ${headerData.consulta.formato.toLowerCase()} em: ${
    headerData.consulta.dataConsulta
  } \n\n`;

  body += generateFileContent(dadosIniciais, exportSelected);

  // Criar um Blob com o conteúdo
  const blob = new Blob([body], { type: "text/plain" });

  // Criar um link para o Blob
  const url = URL.createObjectURL(blob);

  // Criar um elemento <a> para iniciar o download
  const link = document.createElement("a");
  link.href = url;
  link.download = `${title}.txt`; // Nome do arquivo
  document.body.appendChild(link);
  link.click();

  // Remover o link e revogar o URL do Blob
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
